import React from 'react';
import { Trash2, Clock, ShieldAlert, Download, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * @typedef {{id: string, slug?: string, name?: string} | null} TenantSummary
 *
 * @typedef {Object} DeleteTenantDialogProps
 * @property {boolean} open
 * @property {(open: boolean) => void} onOpenChange
 * @property {'hard'|'rtbf'} mode
 * @property {TenantSummary} target
 * @property {string} confirmSlug
 * @property {(v: string) => void} setConfirmSlug
 * @property {number} rtbfYears
 * @property {(v: number) => void} setRtbfYears
 * @property {() => Promise<void>} onHardDelete
 * @property {() => Promise<void>} onScheduleRtbf
 * @property {(t: TenantSummary) => Promise<void>} onExportBeforeDelete
 * @property {string} exporting   // tenant id currently exporting, '' when none
 * @property {boolean} deleting
 */

/**
 * Tenant deletion modal — dual mode:
 *   - 'hard'  → irreversible full wipe (requires slug confirmation)
 *   - 'rtbf'  → legal retention: tenant deactivated, hard-delete in N years
 *
 * @param {DeleteTenantDialogProps} props
 */
export const DeleteTenantDialog = ({
  open, onOpenChange, mode, target,
  confirmSlug, setConfirmSlug,
  rtbfYears, setRtbfYears,
  onHardDelete, onScheduleRtbf,
  onExportBeforeDelete, exporting,
  deleting,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-50 border-slate-200 text-slate-900" data-testid="delete-tenant-dialog">
      <DialogHeader>
        <DialogTitle className="text-slate-900 flex items-center gap-2">
          {mode === 'hard'
            ? <><Trash2 className="w-5 h-5 text-red-600" /> Eliminar cliente definitivamente</>
            : <><Clock className="w-5 h-5 text-amber-600" /> Retenção legal (RTBF)</>}
        </DialogTitle>
        <DialogDescription className="text-slate-500">
          {target?.name} <span className="text-slate-400">/{target?.slug}</span>
        </DialogDescription>
      </DialogHeader>

      {mode === 'hard' ? (
        <div className="space-y-4">
          <Alert className="bg-red-50 border-red-200">
            <ShieldAlert className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-700">
              Esta ação é <strong>irreversível</strong>. Serão eliminados TODOS os dados do cliente:
              utilizadores, denúncias, anexos, mensagens, definições, notificações e logs
              (auditoria e email).
            </AlertDescription>
          </Alert>
          <div>
            <Label className="text-slate-600">
              Para confirmar, escreva o slug do cliente: <strong className="text-slate-900">{target?.slug}</strong>
            </Label>
            <Input
              value={confirmSlug}
              onChange={(e) => setConfirmSlug(e.target.value)}
              placeholder={target?.slug}
              className="bg-white border-slate-300 text-slate-900 font-mono"
              data-testid="confirm-slug-input"
            />
          </div>
          <Button
            type="button" variant="outline"
            onClick={() => onExportBeforeDelete(target)}
            disabled={exporting === target?.id}
            className="w-full border-slate-300 text-slate-700 hover:bg-slate-100"
            data-testid="export-before-delete-btn"
          >
            {exporting === target?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar dados para arquivo legal antes de eliminar
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <Alert className="bg-amber-50 border-amber-200">
            <Clock className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              O cliente fica <strong>inativo</strong> e os dados são retidos pelo período legal.
              A eliminação definitiva e automática ocorre no fim do prazo. Pode cancelar a
              qualquer momento antes disso.
            </AlertDescription>
          </Alert>
          <div>
            <Label className="text-slate-600">Período de retenção legal</Label>
            <Select value={String(rtbfYears)} onValueChange={(v) => setRtbfYears(parseInt(v))}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="rtbf-years-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                <SelectItem value="5" className="text-slate-900 hover:bg-slate-100">5 anos (mínimo legal — Lei 93/2021)</SelectItem>
                <SelectItem value="7" className="text-slate-900 hover:bg-slate-100">7 anos</SelectItem>
                <SelectItem value="10" className="text-slate-900 hover:bg-slate-100">10 anos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
          Cancelar
        </Button>
        {mode === 'hard' ? (
          <Button
            onClick={onHardDelete}
            disabled={deleting || confirmSlug !== target?.slug}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="confirm-hard-delete-btn"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Eliminar definitivamente
          </Button>
        ) : (
          <Button
            onClick={onScheduleRtbf}
            disabled={deleting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="confirm-rtbf-btn"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
            Ativar retenção legal
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
